import { useMemo, useCallback } from 'react';

/**
 * Word limits for Chair's Final Report fields.
 */
export const WORD_LIMITS = {
  gapAnalysis: 200,
  evaluationProcess: 300,
  integrityMatters: 300,
  diversityIssues: 300,
  fnsFeedback: 200,
  generalObservations: 300,
  candidateReport: 600,
  candidateExpertise: 100,
  memberAttendance: 100,
};

const TEXT_FIELDS = [
  'gapAnalysis',
  'evaluationProcess',
  'integrityMatters',
  'diversityIssues',
  'fnsFeedback',
  'generalObservations',
];

/**
 * Count the number of words in a string.
 * Returns 0 for empty/blank strings.
 */
export const countWords = (text) => {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
};

/**
 * Word-count validation for every field in the
 * Chair's Final Report form.
 *
 * ../../param {Object} formData – the full formData state from ChairsReport
 * ../../returns {{
 *   wordCounts: Record<string, { count: number, limit: number, over: boolean }>,
 *   candidateWordCounts: Array<{ count: number, limit: number, over: boolean }>,
 *   hasAnyOverLimit: boolean,
 *   isComplete: boolean,
 *   getFieldStatus: (field: string) => { count: number, limit: number, over: boolean },
 *   getCandidateStatus: (index: number) => { count: number, limit: number, over: boolean },
 * }}
 */
export const useWordCount = (formData) => {
  const wordCounts = useMemo(() => {
    const counts = {};
    TEXT_FIELDS.forEach((field) => {
      const count = countWords(formData[field]);
      const limit = WORD_LIMITS[field];
      counts[field] = { count, limit, over: count > limit };
    });
    return counts;
  }, [
    formData.gapAnalysis,
    formData.evaluationProcess,
    formData.integrityMatters,
    formData.diversityIssues,
    formData.fnsFeedback,
    formData.generalObservations,
  ]);

  const candidateWordCounts = useMemo(
    () =>
      (formData.candidateReports || []).map((cr) => {
        const count = countWords(cr.report);
        const limit = WORD_LIMITS.candidateReport;
        return { count, limit, over: count > limit };
      }),
    [formData.candidateReports],
  );

  const candidateExpertiseCounts = useMemo(
    () =>
      (formData.candidateReports || []).map((cr) => {
        const count = countWords(cr.fieldOfExpertise);
        const limit = WORD_LIMITS.candidateExpertise;
        return { count, limit, over: count > limit };
      }),
    [formData.candidateReports],
  );

  const attendanceCounts = useMemo(
    () =>
      (formData.memberAttendance || []).map((m) => {
        const count = countWords(m.attendance);
        const limit = WORD_LIMITS.memberAttendance;
        return {
          count,
          limit,
          over: count > limit,
          filled: !!m.attendance?.trim(),
        };
      }),
    [formData.memberAttendance],
  );

  const hasAnyOverLimit = useMemo(() => {
    const textOver = Object.values(wordCounts).some((wc) => wc.over);
    const candidateOver = candidateWordCounts.some((wc) => wc.over);
    const expertiseOver = candidateExpertiseCounts.some((wc) => wc.over);
    const attendanceOver = attendanceCounts.some((wc) => wc.over);
    return textOver || candidateOver || expertiseOver || attendanceOver;
  }, [
    wordCounts,
    candidateWordCounts,
    candidateExpertiseCounts,
    attendanceCounts,
  ]);

  const isComplete = useMemo(() => {
    const allTextFilled = TEXT_FIELDS.every((field) => formData[field]?.trim());
    const allCandidatesFilled =
      formData.candidateReports?.length > 0 &&
      formData.candidateReports.every((cr) => cr.report?.trim());
    const allAttendanceFilled =
      formData.memberAttendance?.length > 0 &&
      formData.memberAttendance.every((m) => m.attendance?.trim());

    return (
      allTextFilled &&
      allCandidatesFilled &&
      allAttendanceFilled &&
      !hasAnyOverLimit
    );
  }, [formData, hasAnyOverLimit]);

  const getFieldStatus = useCallback(
    (field) => wordCounts[field] || { count: 0, limit: 0, over: false },
    [wordCounts],
  );

  const getCandidateStatus = useCallback(
    (index) =>
      candidateWordCounts[index] || { count: 0, limit: 0, over: false },
    [candidateWordCounts],
  );

  const getCandidateExpertiseStatus = useCallback(
    (index) =>
      candidateExpertiseCounts[index] || { count: 0, limit: 0, over: false },
    [candidateExpertiseCounts],
  );

  const getAttendanceStatus = useCallback(
    (index) => attendanceCounts[index] || { count: 0, limit: 0, over: false },
    [attendanceCounts],
  );

  return {
    wordCounts,
    candidateWordCounts,
    candidateExpertiseCounts,
    attendanceCounts,
    hasAnyOverLimit,
    isComplete,
    getFieldStatus,
    getCandidateStatus,
    getCandidateExpertiseStatus,
    getAttendanceStatus,
  };
};
